/* =============================================================================
   HAKI storefront experiments — assignment, exposure, order tagging.
   -----------------------------------------------------------------------------
   PLM publishes every RUNNING A/B test to the shop metafield custom.experiments;
   snippets/haki-experiments.liquid emits it as <script type="application/json"
   data-haki-experiments> just before this file. This file:

     1. picks an arm per visitor, DETERMINISTICALLY, from the Shopify visitor
        cookie (_shopify_y — server-set, 365 days, immune to WebKit's 7-day
        script-cookie eviction; see snippets/meta-match-keys.liquid) and the
        test's salt. No server call, no extra storage, PLM can recompute it.
     2. exposes window.hakiExp so a component can ask for its override:
            var cfg = window.hakiExp && hakiExp.config('cart_tiers');
        and report that it actually rendered the variant:
            hakiExp.expose('cart_tiers');
     3. writes the assignment into the cart as attributes (_hk_vid, _hk_exp)
        so it arrives on the order — PLM's order sync reads it back.

   THIS FILE HAS A TWIN: plm/lib/experiments/assignment-core.ts. The hash and the
   bucket walk must be byte-for-byte the same function, and tests/
   experiments-assignment.test.js pins both to the same fixture. Change one,
   change both, regenerate the fixture in the PLM repo, copy it here.

   Loaded synchronously in <head> (a few KB, no deps) so the cart controller can
   read an override the moment it parses its tiers.
   ============================================================================= */
(function () {
  'use strict';

  var VID_LS = 'hk_ext_id';          // same key meta-match-keys.liquid mirrors _shopify_y into
  var EXPOSED_SS = 'hk_exp_seen';    // sessionStorage: which (test) beacons already fired this session
  var PREVIEW_SS = 'hk_exp_preview'; // sessionStorage: a forced arm, for previewing (see forcedArm)

  // ---- hash: FNV-1a 32 over UTF-8 bytes, then murmur3 fmix32 --------------
  function fnv1a32(s) {
    var bytes;
    try { bytes = new TextEncoder().encode(s); } catch (e) { bytes = null; }
    var h = 0x811c9dc5;
    if (bytes) {
      for (var i = 0; i < bytes.length; i++) { h ^= bytes[i]; h = Math.imul(h, 0x01000193) >>> 0; }
    } else {
      // No TextEncoder (very old WebKit): ASCII only, which is what visitor ids are.
      for (var j = 0; j < s.length; j++) { h ^= s.charCodeAt(j) & 0xff; h = Math.imul(h, 0x01000193) >>> 0; }
    }
    return h >>> 0;
  }
  function fmix32(h) {
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b) >>> 0;
    h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35) >>> 0;
    h ^= h >>> 16; return h >>> 0;
  }
  function bucketPoint(visitorId, key, salt) {
    return fmix32(fnv1a32(key + ':' + salt + ':' + visitorId)) / 0x100000000;
  }
  function assign(visitorId, key, salt, arms) {
    var total = 0, i;
    for (i = 0; i < arms.length; i++) total += Math.max(0, arms[i].weight || 0);
    if (total <= 0) return null;
    var p = bucketPoint(visitorId, key, salt) * total, acc = 0;
    for (i = 0; i < arms.length; i++) { acc += Math.max(0, arms[i].weight || 0); if (p < acc) return arms[i].key; }
    return arms[arms.length - 1] ? arms[arms.length - 1].key : null;
  }

  // ---- visitor id (mirrors externalId() in meta-match-keys.liquid) --------
  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }
  function lsGet(k) { try { return localStorage.getItem(k) || ''; } catch (e) { return ''; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function visitorId() {
    var id = lsGet(VID_LS) || getCookie('_shopify_y') || getCookie('shopify_client_id');
    if (!id) {
      try { id = crypto.randomUUID(); } catch (e) { id = 'hk-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10); }
    }
    if (/^[A-Fa-f0-9]{32}$|^[A-Fa-f0-9]{64}$/.test(id)) id = 'hk-' + id; // never hand fbevents a hash-shaped id
    lsSet(VID_LS, id);
    return id;
  }

  // ---- preview override ---------------------------------------------------
  // ?hk_exp=<experiment-key>:<variant-key> pins this browser to one arm so a
  // human can look at each variant on purpose. ?hk_exp=off clears it.
  //
  // A preview is NOT a data point, and this is the whole reason the flag exists
  // rather than "just clear your cookies until you land in the right arm":
  // a forced browser never fires the exposure beacon and never writes the cart
  // attributes, so previewing all three arms cannot add exposures to the running
  // test and cannot attribute a test order to an arm the visitor was not really
  // in. Assignment for everyone else is untouched — this only overrides what
  // THIS browser sees.
  //
  // Kept in sessionStorage so the whole journey stays in the arm (the popup, the
  // game, the pages after it) instead of only the first page load. It dies with
  // the tab.
  function readPreview() {
    var forced = null;
    try {
      var q = String(window.location.search || '');
      var m = q.match(/[?&]hk_exp=([^&#]*)/);
      if (m) {
        var raw = decodeURIComponent(m[1] || '').trim();
        if (!raw || raw === 'off' || raw === '0') {
          try { sessionStorage.removeItem(PREVIEW_SS); } catch (e2) {}
          return null;
        }
        forced = raw;
        try { sessionStorage.setItem(PREVIEW_SS, raw); } catch (e3) {}
      }
      if (!forced) { try { forced = sessionStorage.getItem(PREVIEW_SS) || null; } catch (e4) { forced = null; } }
    } catch (e) { forced = null; }
    if (!forced) return null;
    var bits = forced.split(':');
    if (bits.length !== 2 || !bits[0] || !bits[1]) return null;
    return { key: bits[0], variant: bits[1] };
  }
  var preview = readPreview();

  // ---- config ------------------------------------------------------------
  var node = document.querySelector('[data-haki-experiments]');
  var enabled = !node || node.getAttribute('data-enabled') !== 'false';
  var beaconUrl = node ? node.getAttribute('data-beacon') || '' : '';
  var shopDomain = node ? node.getAttribute('data-shop') || '' : '';
  var list = [];
  try { list = JSON.parse(node.textContent) || []; } catch (e) { list = []; }
  if (!Array.isArray(list)) list = [];

  var vid = enabled ? visitorId() : '';
  var assignments = {};   // experimentKey -> variantKey
  var bySurface = {};     // surface -> { key, variant, config }
  if (enabled) {
    for (var i = 0; i < list.length; i++) {
      var e = list[i];
      if (!e || !e.key || !e.salt || !Array.isArray(e.variants)) continue;
      var arm = assign(vid, e.key, e.salt, e.variants);
      // A forced arm replaces the computed one — but only for an arm that really
      // exists on this experiment, so a stale or mistyped link falls back to the
      // real assignment rather than silently showing nothing.
      if (preview && preview.key === e.key) {
        for (var pv = 0; pv < e.variants.length; pv++) {
          if (e.variants[pv].key === preview.variant) { arm = preview.variant; break; }
        }
      }
      if (!arm) continue;
      assignments[e.key] = arm;
      var v = null;
      for (var k = 0; k < e.variants.length; k++) if (e.variants[k].key === arm) v = e.variants[k];
      // First experiment per surface wins; two on one surface is an operator error.
      if (e.surface && !bySurface[e.surface]) bySurface[e.surface] = { key: e.key, variant: arm, config: v ? v.config : null };
    }
  }

  function encoded() {
    var parts = [];
    for (var key in assignments) if (Object.prototype.hasOwnProperty.call(assignments, key)) parts.push(key + ':' + assignments[key]);
    return parts.join(';');
  }

  // ---- exposure beacon ---------------------------------------------------
  function seen() { try { return JSON.parse(sessionStorage.getItem(EXPOSED_SS) || '{}') || {}; } catch (e) { return {}; } }
  function markSeen(key) { try { var s = seen(); s[key] = 1; sessionStorage.setItem(EXPOSED_SS, JSON.stringify(s)); } catch (e) {} }
  function consentOk() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') return cp.analyticsProcessingAllowed() !== false;
    } catch (e) {}
    return true;
  }
  function send(key, variant, kind) {
    if (!beaconUrl || !vid) return;
    var body = JSON.stringify({ shop: shopDomain, events: [{ exp: key, variant: variant, vid: vid, kind: kind }] });
    try {
      // text/plain keeps sendBeacon preflight-free cross-origin; the server parses the text.
      if (navigator.sendBeacon && navigator.sendBeacon(beaconUrl, new Blob([body], { type: 'text/plain' }))) return;
    } catch (e) {}
    try { fetch(beaconUrl, { method: 'POST', body: body, headers: { 'Content-Type': 'text/plain' }, keepalive: true, mode: 'cors' }).catch(function () {}); } catch (e) {}
  }
  function expose(surface, kind) {
    var s = bySurface[surface];
    if (!s || !consentOk()) return;
    // A preview must not become an exposure. See readPreview().
    if (preview && preview.key === s.key) return;
    kind = kind || 'exposure';
    var k = s.key + '|' + kind;
    if (seen()[k]) return;          // once per session; the server dedupes per visitor for good
    markSeen(k);
    send(s.key, s.variant, kind);
  }

  // ---- cart attributes ---------------------------------------------------
  var syncing = false;
  // Writes attempted this page. A write that "succeeds" but does not stick would
  // otherwise re-fire on every settled cart notify; three tries is plenty.
  var syncAttempts = 0, MAX_SYNC_ATTEMPTS = 3;
  function syncCart(state) {
    // Only when there is a cart to write to and something to write. Shopify
    // keeps attributes on the cart until checkout; underscore keys are hidden
    // from the shopper but arrive on the order as customAttributes.
    if (!vid || syncing || !state || !state.item_count) return;
    if (syncAttempts >= MAX_SYNC_ATTEMPTS) return;
    // Same reason as expose(): a previewed order must not be attributed to an
    // arm this visitor was never really assigned to.
    if (preview) return;
    var want = encoded();
    var have = (state.attributes || {});
    if (have._hk_vid === vid && (have._hk_exp || '') === want) return;
    syncing = true;
    syncAttempts++;
    var attrs = { _hk_vid: vid, _hk_exp: want };
    function done() { syncing = false; }
    try {
      // Through the cart store's serialized write queue, never beside it: this
      // call runs in the same settled notify that auto-adds the free gift, and a
      // raw /cart/update.js racing that /cart/add.js can drop the gift while the
      // drawer still shows it (order #HS120011). The store re-reads the cart after
      // the write, so the drawer and the gift reconcile against the real cart.
      var C = window.HakiCart;
      if (C && typeof C.updateAttributes === 'function') {
        C.updateAttributes(attrs).then(done, done);
        return;
      }
      // No store on this page: write directly, then let a store (if one appears)
      // re-read the real cart instead of trusting its pre-write snapshot.
      fetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).then(function () {
        done();
        try { if (window.HakiCart && window.HakiCart.refresh) window.HakiCart.refresh(); } catch (e) {}
      }, done);
    } catch (e) { syncing = false; }
  }

  window.hakiExp = {
    enabled: enabled,
    /** Non-null when this browser is pinned to an arm for previewing. */
    preview: preview,
    visitorId: function () { return vid; },
    /** The variant key this visitor is in for an experiment key, or null. */
    variant: function (experimentKey) { return assignments[experimentKey] || null; },
    /** The active override for a surface (null for control / no test). */
    config: function (surface) { var s = bySurface[surface]; return s ? s.config : null; },
    /** Which experiment (if any) is live on a surface. */
    active: function (surface) { return bySurface[surface] || null; },
    expose: expose,
    syncCart: syncCart,
    attributes: function () { return vid ? { _hk_vid: vid, _hk_exp: encoded() } : {}; },
    // exposed for the test harness
    _assign: assign, _bucketPoint: bucketPoint, _fnv1a32: fnv1a32
  };
})();
